import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Textarea,
  toast,
} from '@berky/ui'
import { ApiError } from '@berky/auth'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'

import {
  type ExternalRecipientPolicyCheck,
  type ExternalRecipientPolicyMode,
  type ExternalRecipientPolicyUpdate,
  type NotificationStatus,
  useNotificationsApi,
} from './api'

interface NotificationPolicyCardProps {
  status: NotificationStatus
}

const modeLabels: Record<ExternalRecipientPolicyMode, string> = {
  Disabled: 'Deaktiviert',
  AllowedDomains: 'Nur erlaubte Domänen',
  Unrestricted: 'Uneingeschränkt',
}

export function NotificationPolicyCard({ status }: NotificationPolicyCardProps) {
  const api = useNotificationsApi()
  const queryClient = useQueryClient()
  const [address, setAddress] = useState('')
  const [mode, setMode] = useState<ExternalRecipientPolicyMode>(
    status.externalRecipientMode,
  )
  const [domains, setDomains] = useState(status.allowedExternalDomains.join('\n'))
  const [confirmUnrestricted, setConfirmUnrestricted] = useState(false)
  const check = useMutation({
    mutationFn: (value: string) =>
      api.get<ExternalRecipientPolicyCheck>('/recipient-policy', {
        query: { address: value },
      }),
  })
  const save = useMutation({
    mutationFn: (update: ExternalRecipientPolicyUpdate) =>
      api.put('/recipient-policy', update),
    onSuccess: async () => {
      setConfirmUnrestricted(false)
      check.reset()
      await queryClient.invalidateQueries({ queryKey: ['notifications', 'status'] })
      toast.success('Versandrichtlinie gespeichert')
    },
  })

  function policyUpdate(): ExternalRecipientPolicyUpdate {
    return {
      mode,
      allowedDomains:
        mode === 'AllowedDomains'
          ? domains
              .split(/[\n,]/)
              .map((domain) => domain.trim())
              .filter(Boolean)
          : [],
    }
  }

  function submitPolicy(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (mode === 'Unrestricted') {
      setConfirmUnrestricted(true)
    } else {
      save.mutate(policyUpdate())
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const normalized = address.trim()
    if (normalized) check.mutate(normalized)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>E-Mail-Empfänger (extern)</CardTitle>
        <CardDescription>
          „Extern“ bedeutet ohne verknüpftes Plattform-Benutzerkonto. Die Adresse kann
          trotzdem zu BERKY gehören.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Wirksame Richtlinie:</span>
          <StatusBadge
            tone={status.externalRecipientMode === 'Disabled' ? 'warning' : 'info'}
          >
            {modeLabels[status.externalRecipientMode]}
          </StatusBadge>
          <span className="text-muted-foreground">
            {status.hasStoredExternalRecipientPolicy
              ? 'Gespeicherte Administrationsentscheidung'
              : 'Deployment-Konfiguration'}
          </span>
        </div>
        {status.externalRecipientMode === 'AllowedDomains' && (
          <p className="text-sm text-muted-foreground">
            Erlaubte Domänen: {status.allowedExternalDomains.join(', ') || 'keine'}
          </p>
        )}
        {status.externalRecipientConfigurationIssue && (
          <Alert variant="destructive">
            <AlertTitle>Richtlinie unvollständig</AlertTitle>
            <AlertDescription>
              {status.externalRecipientConfigurationIssue}
            </AlertDescription>
          </Alert>
        )}
        <form className="space-y-4" onSubmit={submitPolicy}>
          <FormField label="Versandmodus" htmlFor="external-recipient-mode">
            <Select
              value={mode}
              onValueChange={(value) => setMode(value as ExternalRecipientPolicyMode)}
            >
              <SelectTrigger id="external-recipient-mode" className="w-full sm:w-80">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Disabled">Deaktiviert</SelectItem>
                <SelectItem value="AllowedDomains">Nur erlaubte Domänen</SelectItem>
                <SelectItem value="Unrestricted">Uneingeschränkt</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          {mode === 'AllowedDomains' && (
            <FormField
              label="Erlaubte Domänen"
              htmlFor="external-recipient-domains"
              description="Eine Domäne pro Zeile oder durch Komma getrennt. Mindestens eine gültige Domäne ist erforderlich."
            >
              <Textarea
                id="external-recipient-domains"
                value={domains}
                onChange={(event) => setDomains(event.target.value)}
                rows={3}
              />
            </FormField>
          )}
          {mode === 'Unrestricted' && (
            <Alert variant="destructive">
              <AlertTitle>Uneingeschränkter Versand</AlertTitle>
              <AlertDescription>
                Jede syntaktisch gültige E-Mail-Adresse kann laut Richtlinie Empfänger
                werden. Die Benachrichtigungsart muss externe Empfänger weiterhin
                ausdrücklich erlauben.
              </AlertDescription>
            </Alert>
          )}
          {save.isError && (
            <Alert variant="destructive">
              <AlertTitle>Richtlinie nicht gespeichert</AlertTitle>
              <AlertDescription>
                {save.error instanceof ApiError
                  ? save.error.userMessage()
                  : 'Die Versandrichtlinie konnte nicht gespeichert werden.'}
              </AlertDescription>
            </Alert>
          )}
          <Button type="submit" disabled={save.isPending}>
            Richtlinie speichern
          </Button>
        </form>
        <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={submit}>
          <FormField
            className="min-w-0 flex-1"
            label="Adresse laut Richtlinie prüfen"
            htmlFor="external-recipient-address"
            description="Die Prüfung versendet keine Nachricht."
          >
            <Input
              id="external-recipient-address"
              type="email"
              value={address}
              onChange={(event) => {
                setAddress(event.target.value)
                check.reset()
              }}
              placeholder="name@beispiel.de"
            />
          </FormField>
          <Button
            type="submit"
            variant="outline"
            disabled={!address.trim() || check.isPending}
          >
            Prüfen
          </Button>
        </form>
        {check.isError && (
          <Alert variant="destructive">
            <AlertTitle>Prüfung nicht möglich</AlertTitle>
            <AlertDescription>
              Die Versandrichtlinie konnte nicht geprüft werden.
            </AlertDescription>
          </Alert>
        )}
        {check.data && (
          <Alert variant={check.data.isAllowed ? 'default' : 'destructive'}>
            <AlertTitle>
              {check.data.isAllowed
                ? 'Laut Versandrichtlinie zulässig'
                : 'Laut Versandrichtlinie gesperrt'}
            </AlertTitle>
            <AlertDescription>
              Syntax: {check.data.isSyntacticallyValid ? 'gültig' : 'ungültig'}
              {check.data.domain ? ` · Domäne: ${check.data.domain}` : ''}
              {` · Richtlinie: ${modeLabels[check.data.mode]}`}
              {check.data.reason ? ` · ${check.data.reason}` : ''}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      <ConfirmDialog
        open={confirmUnrestricted}
        onOpenChange={setConfirmUnrestricted}
        title="Uneingeschränkten Versand freigeben?"
        description="Danach ist laut Richtlinie jede syntaktisch gültige E-Mail-Adresse zulässig. Benachrichtigungsarten benötigen weiterhin ihre eigene Freigabe für E-Mail-Empfänger (extern)."
        confirmLabel="Uneingeschränkt freigeben"
        pending={save.isPending}
        onConfirm={() => save.mutate(policyUpdate())}
      />
    </Card>
  )
}
